import { useState } from 'react'
import { Link } from 'react-router-dom'

import { datum } from '../format'
import { assistentPfad, laufPfad } from '../pfade'
import type { Uebersicht, UebersichtImport } from '../typen'

const ART: Record<string, string> = {
  ergebnismenge: 'Produktion',
  wareneingang: 'Wareneingang',
  artikelbestandsliste: 'Bestand',
  inventur_historie: 'Inventur',
}

/**
 * Ein korrigierter Tagesbericht (02.11.2026): die nächtliche Nachprüfung hat für schon gebuchte Tage andere Zahlen
 * aus SelectLine bekommen (spät gebuchte Produktion, 10535 am 21.10.2026). Er bucht sich nie selbst. Erkannt am
 * Dateinamen, den das Backend vergibt (`korrigiert-<Auftrag>`), oder am Satz unter den Gründen.
 */
export function istKorrigiert(i: { dateiname?: string | null; gruende?: string[] }): boolean {
  return Boolean(i.dateiname?.startsWith('korrigiert-')) || (i.gruende ?? []).some((g) => g.startsWith('Korrigierter Tagesbericht'))
}

function titel(i: { dokumenttyp: string; tage?: string | null; dateiname?: string | null; gruende?: string[] }): string {
  const art = istKorrigiert(i) ? 'Korrigierter Tagesbericht' : (ART[i.dokumenttyp] ?? i.dokumenttyp)
  return `${art}${i.tage ? ` ${i.tage}` : ''}`
}

function frage(i: UebersichtImport): string {
  if (istKorrigiert(i)) {
    return (
      `SelectLine hat für schon gebuchte Tage nachträglich anders gebucht - der korrigierte Tagesbericht ist Lauf ${i.id}. ` +
      `${(i.gruende ?? []).join(' ')} Bitte erklären Sie mir die Abweichungen und ob ich ihn freigeben soll (ersetzt die alten Läufe) oder verwerfen (so lassen).`
    )
  }
  // Der gemeinsame Ursachen-Satz einmal - mit 21 Kopien wurde der Link zum Assistenten unnötig lang.
  const ursachen = gemeinsameUrsachen(i.gruende ?? [])
  const gruende = (i.gruende ?? []).map((g) => (ursachen && g.endsWith(ursachen) ? g.slice(0, -ursachen.length).trim() : g))
  return (
    `Die Plattform hat den Lauf ${i.id} aus SelectLine (${titel(i)}) nicht selbst gebucht. Gründe: ${gruende.join(' ')} ` +
    (ursachen ? `${ursachen} ` : '') +
    'Bitte sehen Sie sich den Lauf an, erklären Sie mir, was dahintersteckt, und schlagen Sie vor, was wir tun: freigeben, verwerfen oder angleichen.'
  )
}

const URSACHEN = 'Mögliche Ursachen:'
const SICHTBAR = 6

/**
 * Der Satz „Mögliche Ursachen: …“, wenn ihn alle Gründe gleich tragen - dann steht er einmal unter der Liste statt
 * in jeder Zeile. Am 04.11.2026 (Bestand 03.11., Lauf 218) waren es 21 Absätze mit je demselben langen Satz.
 */
function gemeinsameUrsachen(gruende: string[]): string | null {
  const schluesse = gruende.filter((g) => g.includes(URSACHEN)).map((g) => g.slice(g.indexOf(URSACHEN)))
  return schluesse.length >= 2 && schluesse.every((s) => s === schluesse[0]) ? schluesse[0] : null
}

/** Die Gründe eines Laufs: der gemeinsame Ursachen-Satz einmal, ab sieben Gründen der Rest eingeklappt. */
function Gruende({ gruende }: { gruende: string[] }) {
  const [alle, setAlle] = useState(false)
  const ursachen = gemeinsameUrsachen(gruende)
  const zeilen = ursachen ? gruende.map((g) => (g.endsWith(ursachen) ? g.slice(0, -ursachen.length).trim() : g)) : gruende
  const gezeigt = alle || zeilen.length <= SICHTBAR + 1 ? zeilen : zeilen.slice(0, SICHTBAR)
  return (
    <>
      <ul>
        {gezeigt.map((g, n) => (
          <li key={n}>{g}</li>
        ))}
      </ul>
      {gezeigt.length < zeilen.length && (
        <button className="leise" onClick={() => setAlle(true)}>
          {zeilen.length - gezeigt.length} weitere anzeigen
        </button>
      )}
      {ursachen && <p className="hinweis">{ursachen}</p>}
    </>
  )
}

/**
 * Was aus SelectLine kommt, bucht die Plattform seit dem 18.10.2026 selbst. Hier steht nur, was sie
 * angehalten hat - mit den Gründen und dem Weg in den Assistenten -, und in einer Zeile, was sie ohne
 * Klick übernommen hat.
 */
export default function SelectLineWarnungen({ uebersicht }: { uebersicht: Uebersicht | null }) {
  const aus = (uebersicht?.importe ?? []).filter((i) => i.quelle === 'selectline')
  const angehalten = aus.filter((i) => (i.gruende ?? []).length > 0)
  const wartend = aus.filter((i) => i.wartet)
  const selbst = uebersicht?.selbst_gebucht ?? []
  if (angehalten.length === 0 && wartend.length === 0 && selbst.length === 0) return null

  return (
    <div className="karte datenpruefung" id="selectline-warnungen">
      <div className="leiste">
        <b>Aus SelectLine</b>
        <span className="hinweis">
          {angehalten.length > 0
            ? `${angehalten.length} ${angehalten.length === 1 ? 'Lauf' : 'Läufe'} nicht selbst gebucht - bitte besprechen`
            : 'alles Unauffällige ist gebucht'}
        </span>
      </div>
      {angehalten.length > 0 && (
        <ul className="befund-liste">
          {angehalten.map((i) => (
            <li key={i.id} className={`befund ${istKorrigiert(i) ? 'hinweis' : 'warnung'}`}>
              {istKorrigiert(i) ? <span className="status hinweis">Korrektur</span> : <span className="status warnung">Warnung</span>}
              <div className="befund-text">
                <b>{titel(i)}</b> <span className="hinweis">(Lauf {i.id})</span>
                {istKorrigiert(i) && (
                  <div className="hinweis">
                    SelectLine hat diese Tage nach dem Abruf noch geändert. Freigeben ersetzt die alten Läufe dieser Tage
                    (sie werden zurückgenommen und verworfen), Zurücknehmen bucht sie wieder; Verwerfen heißt: so lassen.
                  </div>
                )}
                <Gruende gruende={i.gruende ?? []} />
                <div className="leiste">
                  <Link className="knopf" to={assistentPfad(frage(i))}>
                    Mit dem Assistenten besprechen
                  </Link>
                  <Link className="hinweis-link" to={laufPfad(i)}>
                    Lauf ansehen
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {wartend.map((i) => (
        <p key={i.id} className="hinweis">
          {titel(i)} (Lauf {i.id}): {i.wartet}
        </p>
      ))}
      {selbst.length > 0 && (
        <p className="hinweis">
          Ohne Klick übernommen:{' '}
          {selbst.map((s, n) => (
            <span key={s.id}>
              {n > 0 && ', '}
              <Link to={laufPfad(s)}>{titel(s)}</Link> ({s.positionen} Pos., {datum(s.gebucht_am.slice(0, 10))})
            </span>
          ))}
          . Jede Buchung lässt sich am Lauf zurücknehmen.
        </p>
      )}
    </div>
  )
}
